import {
  Box,
  ChakraProps,
  Flex,
  Icon,
  Link as ChakraLink,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Text } from '@main/complykit/components/text';
import { Tooltip } from '@main/complykit/components/tooltip';
import { Link, LinkProps, useLinkProps } from '@tanstack/react-router';
import { Focusable } from 'react-aria-components';

export interface SidebarNavItemProps extends LinkProps {
  icon?: React.ElementType;
  hasSubitems?: boolean;
  isOpen?: boolean;
  isSubitem?: boolean;
  isSidebarCollapsed?: boolean;
  showAsTooltip?: boolean;
  /** Collapsed tooltip text for items whose label is not a plain string. */
  tooltipLabel?: string;
  chakraProps?: ChakraProps;
  children: React.ReactNode;
  shortcut?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const SidebarNavItem = ({
  icon,
  hasSubitems = false,
  isOpen = false,
  isSubitem = false,
  isSidebarCollapsed = false,
  showAsTooltip = false,
  tooltipLabel,
  children,
  chakraProps,
  /* Rode only the old rich tooltip; ComplyKit Tooltip content is a string by
   * ruling, so the shortcut chip no longer renders. */
  shortcut: _shortcut,
  onClick,
  ...link
}: SidebarNavItemProps) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const subitemTextColor = useColorModeValue('gray.500', 'gray.200');
  const bgActiveColor = useColorModeValue('blue.50', 'rgba(214, 188, 250, 0.16)');
  const bgHoverColor = useColorModeValue('blue.25', 'rgba(214, 188, 250, 0.10)');
  const textHoverColor = useColorModeValue('purple.800', 'purple.200');
  const borderLeftHoverColor = useColorModeValue('blue.500', 'purple.300');
  let isActiveLink = false;
  const linkProps = useLinkProps(link);

  if (link.to && linkProps.className === 'active') {
    isActiveLink = true;
  }

  const tooltipContent = tooltipLabel ?? (typeof children === 'string' ? children : undefined);
  // With no link around it the Flex itself is the click target, hence a button.
  const isOwnClickTarget = !!onClick && !link.to && !link.href;

  const item = (
    <Flex
      onClick={onClick as React.MouseEventHandler<unknown>}
      align="center"
      px={isSidebarCollapsed ? 2 : 3}
      h="9"
      borderRadius="md"
      /* data-group carries Chakra's _groupHover without spending the ARIA
       * role, which must stay honest for the tooltip's Focusable check. */
      data-group
      role={isOwnClickTarget ? 'button' : undefined}
      color={isSubitem ? subitemTextColor : textColor}
      fontSize={isSubitem ? 'xs' : 'sm'}
      borderLeft={!isSidebarCollapsed ? '2px' : undefined}
      transition="0.3s ease-out"
      borderLeftColor="transparent"
      {...((onClick || link.href) && {
        cursor: 'pointer',
        _hover: {
          bg: bgHoverColor,
          color: textHoverColor,
          transition: '0.3s ease-out',
        },
      })}
      {...(isActiveLink && {
        bg: bgActiveColor,
        color: textHoverColor,
        borderLeftColor: borderLeftHoverColor,
        transition: '0.3s ease-out',
        fontWeight: '600',
      })}
    >
      <Flex justifyContent="space-between" w="100%" alignItems="center">
        <Flex alignItems="center" w="full" textOverflow="ellipsis" whiteSpace="nowrap">
          {icon && (
            <Icon
              mr="4"
              fontSize="lg"
              color={textColor}
              {...((onClick || link.href) && {
                _groupHover: {
                  color: textHoverColor,
                },
              })}
              {...(isActiveLink && {
                color: textHoverColor,
              })}
              as={icon}
            />
          )}
          <Text as="div" size="inherit" isTruncated isGrow>
            {children}
          </Text>
        </Flex>
        {hasSubitems && !showAsTooltip && (
          <Icon
            as={ChevronDownIcon}
            transition="all .25s ease-in-out"
            transform={isOpen ? 'rotate(180deg)' : ''}
            color={textColor}
            {...((onClick || link.href) && {
              _groupHover: {
                color: textHoverColor,
              },
            })}
            {...(isActiveLink && {
              color: textHoverColor,
            })}
            w={3}
            h={3}
          />
        )}
      </Flex>
    </Flex>
  );

  /* Collapsed nav: an explicit ComplyKit Tooltip names the item on the right.
   * The trigger must BE the interactive element (the anchor, or the
   * button-role Flex): Focusable injects tabIndex 0 and checks the role, so
   * seating it on a wrapper inside a link means a second tab stop and a
   * console warning. Focusable is still needed because neither Chakra nor
   * TanStack links consume React Aria context. */
  const withTooltip = (trigger: React.ReactElement) =>
    showAsTooltip && tooltipContent ? (
      <Tooltip content={tooltipContent} placement="right">
        <Focusable>
          {/* The seat's own cast: Focusable admits only string-typed elements. */}
          {trigger as React.ReactElement<React.DOMAttributes<Element>, string>}
        </Focusable>
      </Tooltip>
    ) : (
      trigger
    );

  if (!link.href && !link.to) {
    return withTooltip(item);
  }

  if (link.href) {
    return withTooltip(
      <ChakraLink
        style={{ textDecoration: 'none' }}
        href={link.href}
        isExternal={link.target === '_blank'}
        _focus={{ boxShadow: 'none' }}
        {...chakraProps}
      >
        {item}
      </ChakraLink>,
    );
  }

  return (
    <Box _focus={{ boxShadow: 'none' }} as="span" {...chakraProps}>
      {withTooltip(<Link {...link}>{item}</Link>)}
    </Box>
  );
};
